import { createFileRoute, useParams } from "@tanstack/react-router"
import { checkPermissions } from "@/utils/checkPermissions"
import { useState } from "react"
import { useGroupBaseScheduleQuery } from "@/api/queries/groups/useGroupBaseScheduleQuery"
import { useGroupSuspenseQuery } from "@/api/queries/groups/useGroupSuspenseQuery"
import { useUpdateGroupBaseScheduleMutation } from "@/api/mutations/groups/useUpdateGroupBaseScheduleMutation"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useToast,
  EmptyState,
  PageHeader,
  hasResponseStatus,
} from "toptaal-shared"
import { ScheduleGrid } from "@/components/pages/group/ScheduleGrid"
import { ErrorBoundary } from "react-error-boundary"

export const Route = createFileRoute("/beheer/groepen/$groupId/rooster/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "groups"),
})

interface RoosterContentProps {
  groupId: number
}

function RoosterContent({ groupId }: RoosterContentProps) {
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false)
  const { error: showError } = useToast()
  const updateMutation = useUpdateGroupBaseScheduleMutation(groupId)

  const { data: schedule } = useGroupBaseScheduleQuery({
    groupId,
  })

  const { data: groupData } = useGroupSuspenseQuery({
    groupId,
  })

  const group = groupData.data

  const handleFinalize = async () => {
    try {
      await updateMutation.mutateAsync({
        baseSchedule: {
          finalized: true,
        },
      })
      setShowFinalizeConfirm(false)
    } catch {
      showError("Rooster definitief maken mislukt")
    }
  }

  const allLessonsComplete = schedule.lessons.every(
    (lesson) =>
      lesson.dayOfWeek &&
      lesson.startTime &&
      lesson.endTime &&
      lesson.classroom &&
      lesson.classroom.location?.name &&
      lesson.classroom.number &&
      lesson.teacher,
  )

  const canFinalize =
    schedule.lessons.length === schedule.numberOfLessonsRequired &&
    allLessonsComplete

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <PageHeader>Rooster</PageHeader>

        {!schedule.finalized && (
          <Button
            variant="primary"
            onClick={() => setShowFinalizeConfirm(true)}
            disabled={!canFinalize}
          >
            Definitief maken
          </Button>
        )}
      </div>

      <ScheduleGrid schedule={schedule} group={group} />

      <Dialog open={showFinalizeConfirm} onOpenChange={setShowFinalizeConfirm}>
        <DialogContent className="p-12">
          <DialogHeader>
            <DialogTitle className="mb-4">Rooster definitief maken</DialogTitle>
            <DialogDescription>
              <p className="mb-4">
                Weet je zeker dat je dit rooster definitief wil maken?
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-4 justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowFinalizeConfirm(false)}
            >
              Annuleren
            </Button>
            <Button
              variant="primary"
              onClick={handleFinalize}
              isLoading={updateMutation.isPending}
            >
              Bevestigen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function RouteComponent() {
  const { groupId } = useParams({
    from: "/beheer/groepen/$groupId/rooster/",
  })

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        if (hasResponseStatus(error) && error.response?.status === 404) {
          return <EmptyState>Geen rooster gevonden.</EmptyState>
        }
        throw error
      }}
      resetKeys={[groupId]}
    >
      <RoosterContent groupId={Number(groupId)} />
    </ErrorBoundary>
  )
}
